import { useEffect, useRef, useState } from "react"

export type Position = {
    left: number,
    right: number,
    top: number,
    bottom: number
}

export type Bounds = Position & {
    width: number,
    height: number
}

export type DraggableReturnType<C, H> = {
    position?: Position,
    isDragging: boolean,
    containerRef: React.Ref<C>,
    handleRef: React.Ref<H>
}

const useDraggable = <C extends HTMLElement,H extends HTMLElement>(boundaryElement?: HTMLElement) => {
    const containerRef = useRef<C>(null);
    const handleRef = useRef<H>(null);
    const containerStartPosition = useRef<Bounds>();
    const boundary = useRef<Position>();
    const [state, setState] = useState<{position?: Position, isDragging: boolean}>({isDragging: false})
    const cursorStartPosition = useRef<Position>()
    const currentPosition = useRef<Position>();

    const onMouseMove = (e: MouseEvent) => {
        const cursorStart = cursorStartPosition.current
        const containerStart = containerStartPosition.current
        if (!cursorStart || !containerStart) return;

        const deltaX = e.clientX - cursorStart.left;
        const deltaY = e.clientY - cursorStart.top;

        let newLeft = containerStart.left + deltaX;
        let newTop = containerStart.top + deltaY;

        const containerWidth = containerStart.width;
        const containerHeight = containerStart.height;

        const margin = 40;
        const headerHeight = handleRef.current?.getBoundingClientRect().height ?? 40;
        const minTopOffset = 16;

        newLeft = Math.min(
            window.innerWidth - margin,
            Math.max(-containerWidth + margin, newLeft)
        );

        newTop = Math.min(
            window.innerHeight - headerHeight,
            Math.max(minTopOffset, newTop)
        );

        const newRight = window.innerWidth - newLeft - containerWidth;
        const newBottom = window.innerHeight - newTop - containerHeight;

        const newPosition: Position = {
            left: newLeft,
            top: newTop,
            right: newRight,
            bottom: newBottom
        };

        currentPosition.current = newPosition;
        setState({
            position: newPosition,
            isDragging: true
        })
    }
    const onMouseUp = () => {
        setState({
            position: currentPosition.current,
            isDragging: false
        });
        
        document.removeEventListener("mouseup", onMouseUp)
        document.removeEventListener("mousemove", onMouseMove)
        document.removeEventListener("contextmenu", onMouseUp)
    }
    
    const onMouseDown = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

        const container = containerRef.current
        if (!container) return

        if (e.preventDefault) e.preventDefault()

        const bounds = container.getBoundingClientRect()
        if (boundaryElement) {
            const boundaryElementBounds = boundaryElement.getBoundingClientRect();
            boundary.current = {
                left: boundaryElementBounds.left,
                top: boundaryElementBounds.top,
                right: window.innerWidth - boundaryElementBounds.right,
                bottom: window.innerHeight - boundaryElementBounds.bottom
            }

        }
        containerStartPosition.current = {
            left: bounds.left,
            top: bounds.top,
            right: window.innerWidth - bounds.right,
            bottom: window.innerHeight - bounds.bottom,
            width: bounds.width,
            height: bounds.height
        };
        cursorStartPosition.current = {
            left: e.clientX,
            right: window.innerWidth - e.clientX,
            top: e.clientY,
            bottom: window.innerHeight - e.clientY

        }
        setState({
            position: containerStartPosition.current,
            isDragging: true
        })
        document.addEventListener("mousemove", onMouseMove)
        document.addEventListener("mouseup", onMouseUp)
        document.addEventListener("contextmenu", onMouseUp)
    }

    useEffect(() => {
        const container = containerRef.current;
        const handle = handleRef.current;
        if (container && handle) {
            handle.addEventListener("mousedown", onMouseDown)
        }
        return () => {
            if (container && handle) {
                handle.removeEventListener("mousedown", onMouseDown)
            }
        }
    }, [containerRef, handleRef])

    return {
        ...state,
        containerRef,
        handleRef
    }
}

export default useDraggable